import { useEffect, useRef, useState } from 'react'
import type { Project } from '@shared/types'
import { hashPasswordWebCrypto } from '@tn/shared'
import { useApi } from '../index'
import { generateGccHtml, generateTaskRegisterCsv, generateTaskRegisterJson, generateGccBundle, protectGccHtml } from './generateGccHtml'
import GccExportPasswordDialog from './GccExportPasswordDialog'
import type { GccProtectableExport } from './GccExportPasswordDialog'
import { collectTnAnalyses, generateGccCombinedReport } from './gccCombinedReport'

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const EXPORT_FILE_SUFFIX: Record<GccProtectableExport, string> = {
  zip: 'gcc-bundle.zip',
  report: 'gcc-combined-report.html',
  html: 'gcc-dashboard.html',
  simple: 'gcc-dashboard-simple.html',
}

const EXPORT_FAILED: Record<GccProtectableExport, string> = {
  zip: 'Bundle failed — please try again.',
  report: 'Report failed — please try again.',
  html: 'Download failed — please try again.',
  simple: 'Download failed — please try again.',
}

/** Marks the downloads that can take a password. */
function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </svg>
  )
}

function FileIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M4 1.75h5.5L12.5 4.75V14.25H4z" />
      <path d="M9.25 1.75v3.25h3.25" />
    </svg>
  )
}

type GccData = NonNullable<Project['gccData']>
type GetTnHtml = ((projectId: string) => Promise<string>) | undefined

/** What the menu builds its downloads with. The app uses GCC_EXPORTERS; a test can pass its own (WI 2913). */
export interface GccExporters {
  /** The GCC dashboard, Full or Simple */
  dashboardHtml: (gcc: GccData, project: Project, variant: 'full' | 'simple') => string
  /** The combined report: the GCC dashboard and every linked TN analysis in one file (WI 2486) */
  combinedReport: (gcc: GccData, project: Project, getTnHtml: GetTnHtml, tnProjects: Project[]) => Promise<string>
  /** The ZIP of every file, each HTML behind the gate when there is one (WI 2506) */
  bundle: typeof generateGccBundle
  /** Hash and salt of a download's password */
  hashPassword: typeof hashPasswordWebCrypto
}

export const GCC_EXPORTERS: GccExporters = {
  dashboardHtml: (gcc, project, variant) => generateGccHtml(gcc, project, { variant }),
  combinedReport: async (gcc, project, getTnHtml, tnProjects) =>
    generateGccCombinedReport(generateGccHtml(gcc, project), project, await collectTnAnalyses(project, getTnHtml, tnProjects)),
  bundle: generateGccBundle,
  hashPassword: hashPasswordWebCrypto,
}

interface GccExportMenuProps {
  gcc: GccData
  project: Project
  tnProjects: Project[]
  exporters?: GccExporters
}

/** The dashboard's Export menu: the ZIP, the combined report, both GCC dashboards and the task register. */
export default function GccExportMenu({ gcc, project, tnProjects, exporters = GCC_EXPORTERS }: GccExportMenuProps) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<GccProtectableExport | null>(null)
  const [passwordFor, setPasswordFor] = useState<GccProtectableExport | null>(null)
  const bundling = busy !== null
  const [bundleError, setBundleError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const api = useApi()

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const slug = (project.client ?? 'gcc').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'gcc'

  const handleExport = (type: 'csv' | 'json') => {
    setOpen(false)
    if (type === 'csv') {
      downloadBlob(generateTaskRegisterCsv(gcc), `${slug}-task-register.csv`, 'text/csv;charset=utf-8;')
    } else {
      downloadBlob(generateTaskRegisterJson(gcc), `${slug}-task-register.json`, 'application/json;charset=utf-8;')
    }
  }

  // The HTML and ZIP downloads first ask for an optional password (WI 2506)
  const askPassword = (kind: GccProtectableExport) => {
    setOpen(false)
    setPasswordFor(kind)
  }

  // The ZIP and the combined report (WI 2486) both fetch the linked TN analyses
  const runExport = async (kind: GccProtectableExport, password: string | null) => {
    setPasswordFor(null)
    setBusy(kind)
    setBundleError(null)
    try {
      // The plaintext password goes no further than the hash
      const gate = password ? await exporters.hashPassword(password) : null
      const getTnHtml: GetTnHtml = typeof api.getDashboardHtml === 'function'
        ? (id: string) => api.getDashboardHtml?.(id) ?? Promise.reject(new Error('getDashboardHtml unavailable'))
        : undefined
      const html = (content: string) => new Blob([protectGccHtml(content, gate)], { type: 'text/html;charset=utf-8;' })
      const blob = kind === 'zip'
        ? await exporters.bundle(gcc, project, getTnHtml, tnProjects, gate)
        : kind === 'report'
          ? html(await exporters.combinedReport(gcc, project, getTnHtml, tnProjects))
          : html(exporters.dashboardHtml(gcc, project, kind === 'simple' ? 'simple' : 'full'))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${slug}-${EXPORT_FILE_SUFFIX[kind]}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setBundleError(err instanceof Error ? err.message : EXPORT_FAILED[kind])
    } finally {
      setBusy(null)
    }
  }

  // z-30 keeps the menu and its error above the sticky tab bar (z-index 20, which
  // comes later in the page) when it pins under the toolbar (#2573)
  return (
    <div className="relative" ref={ref}>
      {bundleError && (
        <div className="absolute right-0 top-full mt-1 w-60 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 z-30">
          {bundleError}
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        disabled={bundling}
        className="gx-btn-dark"
      >
        {busy === 'zip' ? '⟳ Bundling…' : busy === 'report' ? '⟳ Building report…' : busy ? '⟳ Preparing…' : '↓ Export'}
        <span className="gx-dim text-[10px]">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="gx-menu absolute right-0 top-full mt-1.5 z-30">
          <div className="gx-menu-head">Bundle</div>
          <button onClick={() => askPassword('zip')} disabled={bundling} className="gx-menu-item">
            <LockIcon /> {bundling ? 'Zipping…' : 'Download ZIP'}
          </button>
          <div className="gx-menu-sep" />
          <div className="gx-menu-head">Combined Report</div>
          <button
            onClick={() => askPassword('report')}
            disabled={bundling}
            title="One HTML file with the GCC design and every TN analysis"
            className="gx-menu-item"
          >
            <LockIcon /> Download Combined Report
          </button>
          <div className="gx-menu-sep" />
          <div className="gx-menu-head">GCC Dashboard</div>
          <button onClick={() => askPassword('html')} disabled={bundling} className="gx-menu-item">
            <LockIcon /> Download GCC Dashboard
          </button>
          <button
            onClick={() => askPassword('simple')}
            disabled={bundling}
            title="For clients: without the Disposition, Sourcing Model, Sequencing and Value Case tabs or their data"
            className="gx-menu-item"
          >
            <LockIcon /> Download GCC Dashboard (Simple)
          </button>
          <div className="gx-menu-sep" />
          <div className="gx-menu-head">Task Register</div>
          <button onClick={() => handleExport('csv')} className="gx-menu-item">
            <FileIcon /> Download CSV
          </button>
          <button onClick={() => handleExport('json')} className="gx-menu-item">
            <FileIcon /> Download JSON
          </button>
        </div>
      )}
      {passwordFor && (
        <GccExportPasswordDialog
          kind={passwordFor}
          onCancel={() => setPasswordFor(null)}
          onConfirm={(password) => { void runExport(passwordFor, password) }}
        />
      )}
    </div>
  )
}
